import {
  Col,
  Row,
  Tag,
  Form,
  List,
  Space,
  Button,
  Select,
  message,
  Skeleton,
  Popconfirm,
  Image,
  Typography,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import dayjs from "dayjs";

import AddNotesOp from "./AddNotesOp";
import { UserContext } from "../../../../context/UserContext";
import useQuery from "../../../../hooks/useQuery";
import {
  parsePageParam,
  updateUrlSearchParams,
} from "../../../../helpers/urlFilters";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";
import filterIcon from "../../../../assets/images/nurseIcons/filter.svg";
import ProgressNotesIcon from "../../../../assets/images/nurseIcons/notes.svg";
import {
  getProgressNotesAPI,
  deleteProgressNoteAPI,
} from "../../../../services/outpatient.services";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

function ProgressMonitoringOp() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);
  const query = useQuery();
  const userId = userDetails?.id;

  const admissionId = query?.aid;
  const selectedRole = query?.role || "all";
  const page = parsePageParam(query);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState({
    status: false,
    type: "add",
    id: 0,
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [limit] = useState(100);
  const [total, setTotal] = useState(0);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function getData() {
  if (!admissionId) return;
  setLoading(true);

  const params = {
    page,
    limit,
  };

  if (selectedRole !== "all") {
    params.role = selectedRole;
  }

  try {
    const res = await getProgressNotesAPI(admissionId, params);

    const responseData = res.data;

    const list =
      responseData?.results ||
      responseData?.data ||
      responseData ||
      [];
    
    setData(list);
    setTotal(responseData?.count || list.length || 0);

  } catch (error) {
    console.error(error);
  } finally {
    setLoading(false);
  }
}


  useEffect(() => {
    if (admissionId) getData();
  }, [selectedRole, admissionId, page]);

  function updateItem(item) {
    const recordTime = item?.record_time
      ? dayjs.utc(item.record_time).local()
      : dayjs();

    if (isMobile) {
      form.setFieldsValue({
        notes: item?.notes,
        record_date: recordTime,
        record_time_only: recordTime,
        record_time: undefined,
      });
    } else {
      form.setFieldsValue({
        notes: item?.notes,
        record_time: recordTime,
      });
    }

    setIsModalOpen({ status: true, type: "update", id: item?.id });
  }

  function addItem() {
    form.resetFields([
      "notes",
      "record_time",
      "record_date",
      "record_time_only",
    ]);
    setIsModalOpen({ status: true, type: "add" });
  }


  async function deleteItem(id) {
    try {
      const res = await deleteProgressNoteAPI(id);
      message.success(res.data.message || "Deleted successfully");
      getData();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to delete");
    }
  }

  const options = [
    { label: "All", value: "all" },
    { label: "Doctor", value: "Doctor" },
    { label: "Nurse", value: "Nurse" },
    { label: "Physiotherapist", value: "Physiotherapist" },
    { label: "Speech Swallow Therapist", value: "Speech_Swallow_Therapist" },
    { label: "Respiratory Therapist", value: "Respiratory_Therapist" },
    { label: "Nutritionist", value: "Nutritionist" },
    { label: "Psychologist", value: "Psychologist" },
  ];

  function canEditOwnNote(item) {
    if (item?.done_by_user_id != null && userId != null) {
      return Number(item.done_by_user_id) === Number(userId);
    }
    // Fallback when API omits done_by_user_id
    const sameFirst =
      (item?.done_by_first_name || "").trim().toLowerCase() ===
      (userDetails?.first_name || "").trim().toLowerCase();
    const sameLast =
      (item?.done_by_last_name || "").trim().toLowerCase() ===
      (userDetails?.last_name || "").trim().toLowerCase();
    const sameRole =
      (item?.done_by_role || "") === (userDetails?.currRole || "");
    return sameFirst && sameLast && sameRole;
  }

  function RenderItem(item) {
    const timestamp = dayjs.utc(item?.record_time).local().format("DD-MM-YYYY h:mm A");
    const showEdit = canEditOwnNote(item);

    return (
      <List.Item
        style={{
          wordWrap: "break-word",
          whiteSpace: "normal",
          overflow: "hidden",
          padding: isMobile ? "8px 4px" : "10px 8px",
          borderBottom: "1px solid #f0f0f0",
        }}
        actions={
          showEdit
            ? [
                <Button
                  key="edit"
                  type="text"
                  onClick={() => updateItem(item)}
                >
                  <EditIcon style={{ fontSize: "14px" }} />
                </Button>,
                <Popconfirm
                  key="delete"
                  title="Delete the note"
                  description="Are you sure to delete this note?"
                  onConfirm={() => deleteItem(item?.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="text">
                    <DeleteOutlined style={{ color: "red", fontSize: "14px" }} />
                  </Button>
                </Popconfirm>,
              ]
            : []
        }
      >
        <Skeleton title={false} loading={loading} active>
          <List.Item.Meta
            title={
  <Space
    direction={isMobile ? "vertical" : "horizontal"}
    align="start"
    style={{ width: "100%" }}
  >
    <span
      style={{
        fontSize: isMobile ? "14px" : "15px",
        fontWeight: 600,
      }}
    >
      {item?.done_by_first_name} {item?.done_by_last_name}
    </span>

    <Tag color="gold">
      {item?.done_by_role?.replaceAll?.("_", " ") || item?.done_by_role}
    </Tag>

    <Tag color="geekblue" style={{ fontWeight: "normal" }}>
      {timestamp}
    </Tag>
  </Space>
}

            description={
              <Typography.Paragraph
                style={{
                  color: "#000",
                  opacity: 0.85,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  fontSize: isMobile ? "13px" : "14px",
                  marginBottom: "0px",
                }}
                ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
              >
                {item?.notes}
              </Typography.Paragraph>
            }
          />
        </Skeleton>
      </List.Item>
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ padding: "10px" }}>
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={24}
        style={{
          display: "flex",
          justifyContent: isMobile ? "center" : "flex-end",
        }}
      >
        <Space
          size={isMobile ? 10 : 8}
          direction={isMobile ? "vertical" : "horizontal"}
          style={{
            width: isMobile ? "100%" : "auto",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <Space
            size={8}
            align="center"
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            <Image
              src={filterIcon}
              preview={false}
              style={{
                width: isMobile ? "20px" : "24px",
                flexShrink: 0,
              }}
            />
            <Select
              options={options}
              value={selectedRole}
              onChange={(val) =>
                updateUrlSearchParams(navigate, {
                  role: val === "all" ? null : val,
                  page: 1,
                })
              }
              style={{
                width: 220,
                minWidth: 220,
              }}
              popupMatchSelectWidth
            />
          </Space>
          <Button
            type="primary"
            onClick={addItem}
            style={{
              width: isMobile ? "100%" : "auto",
              whiteSpace: "nowrap",
            }}
          >
            Add Notes
          </Button>
        </Space>

        {isModalOpen?.status && (
          <AddNotesOp
            form={form}
            admissionId={admissionId}
            getData={getData}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            userDetails={userDetails}
          />
        )}
      </Col>


      <Col
        lg={{ span: 18, offset: 3 }}
        md={{ span: 24 }}
        xs={24}
        style={{ width: "100%" }}
      >
        {loading ? (
          <LoadingIndicator
            style={{ marginBottom: "200px" }}
            message="Loading progress notes, please wait..."
          />
        ) : data?.length > 0 ? (
          <List
            bordered
            dataSource={data}
            itemLayout="horizontal"
            renderItem={RenderItem}
            pagination={
              total > limit
                ? {
                    current: page,
                    pageSize: limit,
                    total,
                    onChange: (p) => updateUrlSearchParams(navigate, { page: p }),
                    showSizeChanger: false,
                    showTotal: (t) => `Total ${t} notes`,
                  }
                : false
            }

            style={{
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              width: "100%",
              overflow: "hidden",
              padding: isMobile ? "6px" : "12px",
            }}
          />
        ) : (
          <Row
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "70vh",
            }}
          >
            <Col span={24} style={{ textAlign: "center" }}>
              <NoDataPage
                title="Currently, No Progress Notes Have Been Added"
                subtitle="Please add new notes or check back later."
                image={ProgressNotesIcon}
              />
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
}

export default ProgressMonitoringOp;
